/**
 * Tenant theme tokens → CSS custom properties (--tenant-*, --bs-*).
 * Consolidated Credit values match legacy custom_variables.scss / custom_styles.scss.
 */

export type TenantThemeFonts = {
	sansFontFamily: string;
	/** h1–h6 / .h1–.h6 (Bootstrap %heading). Match sans when headings use the same face as body. */
	headingFontFamily: string;
	accentFontFamily: string;
	/** Injected as <link rel="stylesheet"> (e.g. Typekit, Google Fonts). */
	externalStylesheetUrls: string[];
};

export type TenantThemeColors = {
	primary: string;
	primaryHover: string;
	primaryAlpha10: string;
	primaryAlpha65: string;
	primaryAlpha90: string;
	textHeading: string;
	textBody: string;
	pageBackground: string;
	pale: string;
	paleAlpha15: string;
	paleAlpha20: string;
	grey: string;
	greyAlpha9: string;
	success: string;
	danger: string;
	/** Modal / info-block headings (legacy $blue-800 relative to brand). */
	headingAccent: string;
	chatbotChrome: string;
	chatbotBubble: string;
	stepperDisabledCircle: string;
};

export type TenantTheme = {
	colors: TenantThemeColors;
	fonts: TenantThemeFonts;
};

/** Consolidated Credit (US + CA): current production branding. */
export const consolidatedCreditTenantTheme: TenantTheme = {
	colors: {
		primary: "#287dad",
		primaryHover: "#246184",
		primaryAlpha10: "rgba(40, 125, 173, 0.1)",
		primaryAlpha65: "rgba(40, 125, 173, 0.65)",
		primaryAlpha90: "rgba(40, 125, 173, 0.9)",
		textHeading: "#003c73",
		textBody: "#212529",
		pageBackground: "#e6eaf0",
		pale: "#98a5b9",
		paleAlpha15: "rgba(152, 165, 185, 0.15)",
		paleAlpha20: "rgba(152, 165, 185, 0.2)",
		grey: "#455162",
		greyAlpha9: "rgba(69, 81, 98, 0.9)",
		success: "#4ec310",
		danger: "#d63384",
		headingAccent: "#165a7d",
		chatbotChrome: "#dbe0e8",
		chatbotBubble: "#e6eaf0",
		stepperDisabledCircle: "#98a5b9",
	},
	fonts: {
		sansFontFamily: "nunito-sans, sans-serif",
		headingFontFamily: "nunito-sans, sans-serif",
		accentFontFamily: "\"arvo\", serif",
		externalStylesheetUrls: ["https://use.typekit.net/ijq1xpe.css"],
	},
};

/**
 * New Leaf Debt Solutions — colors from brand palette (hex swatches).
 * Greens: #021201, #156011, #729E6F, #BCDCBA, #F7FFF5.
 * Neutrals: #010F01, #5A7C5A, #AED2AC, #DFE4DF, #FCFFFC.
 */
export const newLeafTenantTheme: TenantTheme = {
	colors: {
		primary: "#156011",
		primaryHover: "#104a0d",
		primaryAlpha10: "rgba(21, 96, 17, 0.1)",
		primaryAlpha65: "rgba(21, 96, 17, 0.65)",
		primaryAlpha90: "rgba(21, 96, 17, 0.9)",
		textHeading: "#021201",
		textBody: "#010F01",
		pageBackground: "#DFE4DF",
		pale: "#729E6F",
		paleAlpha15: "rgba(174, 210, 172, 0.15)",
		paleAlpha20: "rgba(174, 210, 172, 0.2)",
		grey: "#5A7C5A",
		greyAlpha9: "rgba(90, 124, 90, 0.9)",
		success: "#729E6F",
		danger: "#b42318",
		headingAccent: "#156011",
		// Slightly darker than pageBackground (#DFE4DF) for the chatbot rail.
		chatbotChrome: "#cfd4cf",
		chatbotBubble: "#F7FFF5",
		stepperDisabledCircle: "#AED2AC",
	},
	fonts: {
		sansFontFamily: "\"Comme\", sans-serif",
		headingFontFamily: "\"Brawler\", serif",
		accentFontFamily: "\"Comme\", sans-serif",
		externalStylesheetUrls: [
			"https://fonts.googleapis.com/css2?family=Brawler:wght@400;700&family=Comme:wght@300;400;500;600;700&display=swap",
		],
	},
};
